import { ContentType } from '@pages/Articles/Articles.types.ts';

import { Article } from './enums';

export const reduxStoreInterfaceArticle = {
  id: 'redux-store-interface-001',
  slug: 'redux-store-interface',
  title: 'Зачем создавать свои интерфейсы TS в Redux: не копируй бэкенд',
  date: '2026-02-12',
  author: Article.Author,
  content: [
    {
      type: ContentType.Paragraph,
      content:
        'Привет! Как frontend разработчик часто вижу ошибку новичков: копируют API-интерфейсы прямо в redux store:',
    },
    {
      type: ContentType.Code,
      content: `"UserResponse { id: string, name: string, createdAt: string }" → state`,
    },
    {
      type: ContentType.Paragraph,
      content: `Видел проекты, где из-за этого store раздувался до 50MB. Бэк меняет схему - фронт в панике. Покажу, как делать собственные красивые интерфейсы, redux-friendly. Результат: рефакторинг в 2 раза быстрее, баги -60%`,
    },
    {
      type: ContentType.Heading,
      content: 'Проблемы копирования API-интерфейсов',
    },
    {
      type: ContentType.Code,
      content: `// ❌ ПЛОХО: Api-response как есть
interface UserResponse {
  id: string;
  name: string;
  email: string;
  createdAt: string;     // Не нужно в UI
  updatedAt: string;     // Не нужно в UI  
  meta: { 
    version: number
  }; // Бэкенд мусор
  permissions: string[]; // Nested arrays = hell
}

interface UserState { 
  entities: UserResponse[]; // 5MB JSON в сторе!
  loading: boolean;
}
`,
    },
    {
      type: ContentType.List,
      listTitle: 'Боли:',
      listItems: [
        'Размер стора: createdAt, meta, timestamps × 1000 users = гигабайт',
        'Нормализация: permissions: string[] → отдельный slice',
        'Бэкенд-изменения: Добавили avatarUrl → переписывай селекторы или ищи где обращался к стору',
        'Diffing: react ререндерит на изменения updatedAt',
      ],
    },
    {
      type: ContentType.Paragraph,
      content: `В моем проекте это убивало perf: 400ms ререндеры на список пользователей. А прикиньте ситуацию, когда \`updatedAt\` обновляется каждую секунду через WebSocket... страшно представить)`,
    },
    {
      type: ContentType.Heading,
      content: 'Создаём собственные типы',
    },
    {
      type: ContentType.Paragraph,
      content: 'Только то, что нужно UI:',
    },
    {
      type: ContentType.Code,
      content: `// ✅ ХОРОШО: Redux интерфейс
interface User {
  id: string;
  name: string;
  email: string;
  hasAvatar: boolean;  // Normalized: avatar === null ? false : true
  role: 'admin' | 'user' | 'guest'; // Normalized permissions, а лучше в enums вынести конечно :3
}

interface UserPermissions { // Отдельный slice
  [userId: string]: ('read' | 'write' | 'delete')[];
}

interface UserState {
  entities: Record<string, User>;  // Normalized: { id: User }
  ids: string[];                   // Список id для order
  loading: boolean;
  currentUserId?: string;
}
`,
    },
    {
      type: ContentType.Paragraph,
      content: `Почему \`hasAvatar: boolean\` лучше чем \`avatar: string | null\`? Потому что UI всегда знает, что делать с boolean: показать аватар или заглушку. А с \`null\` еще и проверку писать надо. Мелочь, а в 1000 местах - уже раздражает`,
    },
    {
      type: ContentType.Heading,
      content: 'Полный кейс: UserList компонент',
    },
    {
      type: ContentType.Code,
      content: `const UserList = () => {
  const users = useAppSelector(selectAllUsers); // User[]
  const userEntities = useAppSelector(selectUserEntities); // Record<string, User>
  
  return (
    <ul>
      {users.map(user => (
        <UserRow 
          key={user.id}
          user={user} 
          permissions={selectUserPermissions(user.id)}
        />
      ))}
    </ul>
  );
};
`,
    },
    {
      type: ContentType.Paragraph,
      content: `Вот и вся магия: React ререндерит список только когда реально меняется name или role. А не когда бэкенд решил обновить timestamp`,
    },
    {
      type: ContentType.Heading,
      content: 'Сравнение до/после (Метрики проекта)',
    },
    {
      type: ContentType.Code,
      content: `| Аспект              | Api Response | Redux Interface | Выигрыш |
| ------------------- | ------------ | --------------- | ------- |
| State size          | 2.1 MB       | 180 KB          | ~10x     |
| Bundle impact       | +15%         | +1%             | ~15x     |
| Selector recomputes | 42/рендер    | 3/рендер        | ~14x     |
`,
    },
    {
      type: ContentType.Paragraph,
      content: `Попробуй если есть список из 100+ элементов. Разница в DevTools Profiler точно удивит`,
    },
    {
      type: ContentType.List,
      listTitle: 'Что я вынес для себя:',
      listItems: [
        'Никогда не копируй Api интерфейсы → [Redux TS Style Guide]',
        'Нормализуй Record<id, Entity> → [Normalizr patterns]',
        'Enums вместо string unions → [Action types best practices]',
        'Lint: @typescript-eslint/ban-types → ESLint TS rules',
      ],
    },
    {
      type: ContentType.Ad,
      content: 'Ссылка на телеграм канал',
      description:
        'Делюсь опытом и статьями по фронтенд-разработке, все анонсы там :3',
      linkUrl: 'https://t.me/wazzupjohnny_channel',
      imageUrl:
        'https://upload.wikimedia.org/wikipedia/commons/thumb/8/82/Telegram_logo.svg/1280px-Telegram_logo.svg.png?20220101141644',
    },
  ],
  externalLinks: [
    {
      label: 'Типизированные хуки',
      url: 'https://react-redux.js.org/using-react-redux/usage-with-typescript/',
    },
    {
      label: 'Паттерны типизации RTK',
      url: 'https://redux-toolkit.js.org/usage/usage-with-typescript',
    },
    {
      label: 'Паттерны типизации',
      url: 'https://redux.js.org/usage/usage-with-typescript',
    },
    {
      label: 'Reddit: RTK + TS оптимальная типизация - опыт senior dev',
      url: 'https://www.reddit.com/r/reactjs/comments/1dy8pox/using_reduxtoolkit_with_typescript_react_whats/',
    },
    {
      label: 'DEV.to: Практические примеры',
      url: 'https://dev.to/tris909/how-to-use-redux-with-typescript-1oag',
    },
  ],
};
